import { getStatusLabel } from '../../../status-labels';
import type { SalesOrderStatus } from '../../../types';
import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';
import { SALES_ORDER_LINE_ITEMS_EDITABLE_STATUSES } from './salesOrderLineItem';

/**
 * A pallet on a sales order: open while its order's products are editable, locked once Confirm Ship mints its SSCC.
 *
 *   view ──────────────────────────────────────▶ allow
 *   add · edit · delete ──▶ SSCC minted? ── yes ──▶ ✗ PALLET_SSCC_LOCKED
 *                                └─ no ──▶ order editable? ── no ──▶ ✗ PALLET_SALES_ORDER_LOCKED
 *                                                └─ yes ──▶ allow
 *
 * `edit` is any attach or detach that puts a line item on the pallet or takes one off it.
 */
export type PalletFacts = { ssccMinted: boolean; salesOrderStatus: SalesOrderStatus };

export const PALLET_FIELDS = [] as const;

export type PalletField = (typeof PALLET_FIELDS)[number];

export const toPalletFacts = ({
  sscc,
  salesOrderStatus,
}: {
  sscc: string | null | undefined;
  salesOrderStatus: SalesOrderStatus;
}): PalletFacts => ({ ssccMinted: typeof sscc === 'string' && sscc.length > 0, salesOrderStatus });

const decideEntity = (facts: PalletFacts, action: Action<PalletField>): Decision => {
  if (action.type === 'view') {
    return allow();
  }

  if (facts.ssccMinted) {
    return deny('PALLET_SSCC_LOCKED', 'This pallet already has its SSCC, so its line items are locked.');
  }

  if (!SALES_ORDER_LINE_ITEMS_EDITABLE_STATUSES.includes(facts.salesOrderStatus)) {
    return deny(
      'PALLET_SALES_ORDER_LOCKED',
      `Pallets are locked once the sales order is ${getStatusLabel(facts.salesOrderStatus)}.`
    );
  }

  return allow();
};

export const palletPolicy: Policy<PalletFacts, PalletField> = definePolicy<PalletFacts, PalletField>({
  kind: 'pallet',
  fields: PALLET_FIELDS,
  ops: [],
  requires: {},
  entity: decideEntity,
});
